import { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Button,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormHelperText,
  FormErrorMessage,
  Icon,
  Flex,
  Container,
  Heading,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { supabase } from '../supabaseClient';
import IMask from 'imask';
import { getClientIP } from '../utils/ipUtils';

const CONSENT_MESSAGE = `I consent to receive text messages from the Rotary Club at the mobile number provided. I understand that message and data rates may apply, and I can opt out at any time by replying STOP to any message received.`;

const PublicConsentForm = () => {
  const [fullName, setFullName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [memberNotFound, setMemberNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedPhone, setSubmittedPhone] = useState('');
  const [hasConsented, setHasConsented] = useState(false);
  const [clubName, setClubName] = useState('Rotary Club');
  const inputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    // Fetch club name from club_preferences
    const fetchClubName = async () => {
      try {
        const { data, error } = await supabase
          .from('club_preferences')
          .select('club_name')
          .single();
        
        if (error) throw error;
        if (data?.club_name) {
          setClubName(data.club_name);
        }
      } catch (error) {
        console.error('Error fetching club name:', error);
      }
    };

    fetchClubName();
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      const maskOptions = {
        mask: '(000) 000-0000',
        lazy: false,
      };
      const mask = IMask(inputRef.current, maskOptions);
      mask.on('accept', () => {
        setPhoneNumber(mask.unmaskedValue);
        setMemberNotFound(false);
      });

      return () => {
        mask.destroy();
      };
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasConsented) {
      toast({
        title: 'Consent Required',
        description: 'Please check the consent box to proceed.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      // Format phone number to match database format: +1 followed by 10 digits, no special characters
      const digitsOnly = phoneNumber.replace(/\D/g, ''); // Remove all non-digits
      console.log('Phone number after removing non-digits:', digitsOnly);
      
      const formattedPhone = `+1${digitsOnly}`; // Add +1 prefix
      console.log('Final formatted phone number:', formattedPhone);

      // Log the query we're about to make
      console.log('Making Supabase query:', {
        table: 'texting_contacts',
        columns: 'id, phone_number, first_name, last_name, full_name, consent_status',
        where: `phone_number = ${formattedPhone}`
      });

      // Find member by phone number - only select needed fields
      const { data: initialMembers, error: memberError } = await supabase
        .from('texting_contacts')
        .select('id, phone_number, first_name, last_name, full_name, consent_status')
        .eq('phone_number', formattedPhone);

      if (memberError) {
        console.error('Error during phone lookup:', memberError);
        console.error('Error details:', {
          message: memberError.message,
          details: memberError.details,
          hint: memberError.hint,
          code: memberError.code
        });
        throw memberError;
      }

      console.log('Phone lookup results:', {
        found: initialMembers && initialMembers.length > 0,
        count: initialMembers ? initialMembers.length : 0,
        results: initialMembers
      });
      
      if (!initialMembers || initialMembers.length === 0) {
        console.log('No member found with phone number:', formattedPhone);
        setMemberNotFound(true);
        setIsLoading(false);
        return;
      }

      const member = initialMembers[0];
      console.log('Found member:', {
        id: member.id,
        phone: member.phone_number,
        status: member.consent_status
      });

      const clientIP = await getClientIP();

      // Record consent
      const { error: consentError } = await supabase
        .from('consent_history')
        .insert({
          member_id: member.id,
          consented_by_name: fullName,
          status: 'CONSENTED',
          ip_address: clientIP,
          consent_message: CONSENT_MESSAGE,
          phone_number: member.phone_number,
        });

      if (consentError) throw consentError;

      // Update member's consent status
      const { error: updateError } = await supabase
        .from('texting_contacts')
        .update({ consent_status: 'CONSENTED' })
        .eq('id', member.id);

      if (updateError) throw updateError;

      setIsSubmitted(true);
      setSubmittedPhone(phoneNumber);
      
      toast({
        title: 'Consent Recorded',
        description: 'Your consent has been successfully recorded.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error recording consent:', error);
      toast({
        title: 'Error',
        description: 'There was an error recording your consent. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (phone.startsWith('+1')) {
      phone = phone.substring(2);
    }
    return `+1 (${phone.substring(0,3)}) ${phone.substring(3,6)}-${phone.substring(6)}`;
  };

  if (isSubmitted) {
    return (
      <Container maxW="container.sm" py={10}>
        <Box textAlign="center" py={10} px={6}>
          <Icon as={CheckCircleIcon} boxSize={'50px'} color={'green.500'} />
          <Heading as="h2" size="xl" mt={6} mb={2}>
            Consent Recorded
          </Heading>
          <VStack spacing={6} align="center">
            <Text color={'gray.700'}>
              Thank you! Your consent has been successfully recorded for the phone number {formatPhoneNumber(submittedPhone)}.
            </Text>
            
            <Text color={'gray.700'}>
              You can now receive text messages from the Rotary Club. You can revoke your consent and stop receiving messages at any time by replying STOP to any message received.
            </Text>
            
            <Text color={'gray.500'}>
              You may now close this browser window.
            </Text>
          </VStack>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxW="container.sm" py={10}>
      <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading as="h1" size="xl" mb={2}>
            {clubName}
          </Heading>
          <Heading as="h2" size="lg" mb={4} color="gray.600">
            Text Message Consent Form
          </Heading>
          <Text color="gray.600">
            Please provide your information to consent to receiving text messages.
          </Text>
        </Box>

        <Box as="form" onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Full Name</FormLabel>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
              />
            </FormControl>

            <FormControl isRequired isInvalid={memberNotFound}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                ref={inputRef}
                placeholder="(000) 000-0000"
                isInvalid={memberNotFound}
                bg="white"
              />
              <FormHelperText>
                Enter the phone number that was registered with the Rotary Club shown in your consent email.
              </FormHelperText>
              {memberNotFound && (
                <FormErrorMessage>
                  No member found with this phone number. Please check the number and try again.
                </FormErrorMessage>
              )}
            </FormControl>

            <FormControl isRequired>
              <Checkbox
                isChecked={hasConsented}
                onChange={(e) => setHasConsented(e.target.checked)}
                size="md"
              >
                {CONSENT_MESSAGE}
              </Checkbox>
              <FormHelperText mt={2}>
                You must click the checkbox to consent to receive text messages from the club.
              </FormHelperText>
            </FormControl>

            <Button
              type="submit"
              colorScheme="blue"
              width="full"
              isLoading={isLoading}
              loadingText="Submitting"
            >
              Submit Consent
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};

export default PublicConsentForm;
